import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ChevronRight, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { api, V1 } from "@/lib/api";

function renderMarkdown(md) {
  if (!md) return null;
  return md.split("\n").map((line, i) => {
    const t = line.trim();
    if (t.startsWith("# ")) return <h1 key={i} className="font-display text-3xl font-bold mt-8 mb-3">{t.slice(2)}</h1>;
    if (t.startsWith("## ")) return <h2 key={i} className="font-display text-2xl font-bold mt-6 mb-2">{t.slice(3)}</h2>;
    if (t.startsWith("### ")) return <h3 key={i} className="font-display text-xl font-bold mt-4 mb-2">{t.slice(4)}</h3>;
    if (t.startsWith("- ")) return <li key={i} className="ml-6 list-disc my-1">{t.slice(2)}</li>;
    if (t === "") return null;
    return <p key={i} className="my-3 leading-relaxed text-foreground/90">{t}</p>;
  });
}

export default function CityGuide() {
  const { il, ilce } = useParams();
  const [data, setData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const load = async () => {
    setStatus("loading");
    try {
      const url = ilce
        ? `${V1}/blog/guides/${encodeURIComponent(il)}?district=${encodeURIComponent(ilce)}`
        : `${V1}/blog/guides/${encodeURIComponent(il)}`;
      const { data: d } = await api.get(url, { timeout: 90000 });
      setData(d);
      if (d.schools) setSchools(d.schools);
      setStatus("ready");
    } catch (e) {
      setStatus("error");
    }
    // best-effort: also load schools by location
    const p = new URLSearchParams({ province: capitalize(il), limit: "20" });
    if (ilce) p.set("district", capitalize(ilce));
    api.get(`${V1}/schools?${p.toString()}`).then((r) => {
      if ((r.data.items || []).length > 0) setSchools(r.data.items);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [il, ilce]);

  const locLabel = ilce ? `${capitalize(ilce)} / ${capitalize(il)}` : capitalize(il);

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 text-center" data-testid="city-guide-loading">
        <div className="animate-pulse text-muted-foreground">Yapay zekâ {locLabel} için rehberi hazırlıyor…</div>
        <div className="text-xs text-muted-foreground mt-2">Bu işlem 20-40 saniye sürebilir.</div>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="city-guide-error">
        <AlertTriangle className="h-10 w-10 text-accent mx-auto mb-3" />
        <div className="font-display text-2xl font-bold mb-2">Rehber şu an oluşturulamadı</div>
        <div className="text-muted-foreground text-sm mb-6">
          {locLabel} rehberi hazırlanırken beklenmedik bir hata oluştu. Lütfen tekrar deneyin ya da
          <Link to="/okullar" className="text-primary underline ml-1">okul dizinini</Link> inceleyin.
        </div>
        <div className="flex gap-2 justify-center">
          <Button onClick={load} data-testid="city-guide-retry"><RefreshCw className="h-4 w-4" /> Tekrar Dene</Button>
          <Button asChild variant="outline"><Link to={`/okullar?il=${encodeURIComponent(capitalize(il))}`}>Okulları Gör</Link></Button>
        </div>
      </div>
    );
  }

  const canonical = ilce ? `/bursluluk/${il}/${ilce}` : `/bursluluk/${il}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.title,
    description: data.seo_description || data.excerpt,
    datePublished: data.created_at,
    dateModified: data.updated_at,
    author: { "@type": "Organization", name: "SanoStudent" },
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12" data-testid="city-guide-page">
      <SEO
        title={data.seo_title || `${locLabel} Bursluluk Sınavı Rehberi`}
        description={data.seo_description || data.excerpt}
        canonical={canonical}
        type="article"
        jsonLd={jsonLd}
      />
      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
        <Link to="/" className="hover:text-foreground">Ana Sayfa</Link>
        <ChevronRight className="h-3 w-3" />
        <span>Bursluluk Rehberleri</span>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground">{locLabel}</span>
      </div>
      <div className="overline text-accent mb-3 flex items-center gap-1"><MapPin className="h-3 w-3" /> İL / İLÇE REHBERİ</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{data.title}</h1>
      <p className="text-muted-foreground mt-4 max-w-3xl leading-relaxed">{data.excerpt}</p>

      <div className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 prose max-w-none">{renderMarkdown(data.content)}</div>
        <aside className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="overline text-accent mb-3">{locLabel}'de Bursluluk Sınavı Açan Okullar</div>
            <div className="space-y-3">
              {schools.length === 0 && <div className="text-sm text-muted-foreground">Bu bölgede henüz kayıtlı okul yok.</div>}
              {schools.slice(0, 6).map((s) => (
                <Link key={s.id} to={`/okul/${s.slug}`} className="block p-3 rounded border border-border hover:border-primary/40">
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.province} / {s.district}</div>
                  <div className="font-medium mt-1">{s.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1 mt-1">{s.short_intro}</div>
                </Link>
              ))}
            </div>
            {schools.length > 6 && (
              <Button asChild variant="outline" size="sm" className="w-full mt-4">
                <Link to={`/okullar?il=${encodeURIComponent(capitalize(il))}`}>Tüm Okulları Gör <ChevronRight className="h-3 w-3" /></Link>
              </Button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function capitalize(s) {
  if (!s) return s;
  const lower = s.toLowerCase();
  const map = { istanbul: "İstanbul", izmir: "İzmir", "sanliurfa": "Şanlıurfa", "diyarbakir": "Diyarbakır", "sirnak": "Şırnak", "mus": "Muş", "usak": "Uşak" };
  if (map[lower]) return map[lower];
  return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
}
